import { stopAnimation, startAnimation } from './animationControls';
import { applyParticleRuntimeHidden } from '../ThreeJsUtils/assetVisibilityUtils';
import store from '../../redux/store';

function clearLifeTimeout(obj) {
  try {
    if (obj && obj.userData && obj.userData._lifeTimeoutId) {
      clearTimeout(obj.userData._lifeTimeoutId);
      obj.userData._lifeTimeoutId = null;
    }
  } catch (e) {
    // ignore
  }
}

/** Particle system life expiry only runs in Preview — never in Design. */
function isParticleLifecycleEnabled() {
  try {
    return store.getState()?.window?.window_type === 'preview';
  } catch (_) {
    return false;
  }
}

/**
 * True when lifecycle must not expire (Design mode, or explicitly suspended).
 * Stars / setLifeTimeout use this to skip countdown and hide.
 */
function isParticleLifecycleSuspendedInDesign(obj) {
  if (!isParticleLifecycleEnabled()) {
    return true;
  }
  if (!obj) return false;
  try {
    if (obj.userData?._lifecycleSuspendedBySelection === true) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

function expireParticleLifecycle(obj) {
  if (!obj) return;
  // Never expire outside Preview.
  if (!isParticleLifecycleEnabled()) return;

  try {
    stopAnimation(obj, obj.name);
  } catch (e) {}
  applyParticleRuntimeHidden(obj, true);
  if (obj.userData) {
    obj.userData.lifeExpired = true;
    obj.userData.isStopped = true;
    obj.userData.isPlaying = false;
    if (typeof obj.userData.remainingLife === 'number') {
      obj.userData.remainingLife = 0;
    }
  }
}

function setLifeTimeout(obj, seconds) {
  clearLifeTimeout(obj);
  if (!obj) return;

  if (obj.userData) {
    obj.userData.lifeExpired = false;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      obj.userData.life = seconds;
      obj.userData.remainingLife = seconds;
    }
  }

  if (typeof seconds !== 'number' || seconds <= 0) return;

  // Design (and any non-preview mode): store life only — do not schedule expiry.
  if (!isParticleLifecycleEnabled()) {
    if (obj.userData) {
      obj.userData._lifecycleSuspendedBySelection = false;
      obj.userData.lifeExpired = false;
      obj.userData.isExpired = false;
    }
    return;
  }

  try {
    obj.userData._lifeTimeoutId = setTimeout(() => {
      try {
        // Left Preview before the timer fired — keep particle alive.
        if (
          !isParticleLifecycleEnabled() ||
          isParticleLifecycleSuspendedInDesign(obj)
        ) {
          obj.userData._lifeTimeoutId = null;
          const life =
            parseLifeSeconds(obj.userData?.life) ??
            (typeof seconds === 'number' ? seconds : null);
          if (life != null && life > 0) {
            obj.userData.remainingLife = life;
          }
          obj.userData.lifeExpired = false;
          return;
        }
        expireParticleLifecycle(obj);
      } catch (e) {}
    }, Math.max(0, seconds) * 1000);
  } catch (err) {
    console.warn('Failed to set life timeout for particle system', err);
  }
}

/** Call when a particle becomes the active design selection. */
function suspendParticleLifecycleForSelection(obj) {
  if (!obj) return;
  obj.userData = obj.userData || {};
  obj.userData._lifecycleSuspendedBySelection = true;
  clearLifeTimeout(obj);
  const life = parseLifeSeconds(obj.userData.life);
  if (life != null && life > 0) {
    obj.userData.remainingLife = life;
  }
  obj.userData.lifeExpired = false;
  obj.userData.isExpired = false;
  obj.userData.runtimeHidden = false;
}

/**
 * Call when the particle is no longer the active selection.
 * In Design, lifecycle stays off for all particles — do not restart timers.
 */
function resumeParticleLifecycleAfterSelection(obj) {
  if (!obj?.userData) return;
  obj.userData._lifecycleSuspendedBySelection = false;

  if (!isParticleLifecycleEnabled()) {
    clearLifeTimeout(obj);
    const life = parseLifeSeconds(obj.userData.life);
    if (life != null && life > 0) {
      obj.userData.remainingLife = life;
    }
    obj.userData.lifeExpired = false;
    obj.userData.isExpired = false;
    return;
  }

  const life = parseLifeSeconds(obj.userData.life);
  if (life != null && life > 0 && obj.userData.lifeExpired !== true) {
    setLifeTimeout(obj, life);
  }
}

function parseLifeSeconds(value) {
  if (value === '' || value === null || typeof value === 'undefined') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * ParticleComponent emits the full particle object on every field edit, so
 * `life` is always present. Only restart the system lifecycle when the life
 * value actually changed — otherwise property edits revive expired particles.
 *
 * @returns {boolean} true when lifecycle was restarted / cleared
 */
function applyLifeTimeoutIfChanged(obj, nextLife, previousLife, options = {}) {
  if (!obj) return false;

  const next = parseLifeSeconds(nextLife);
  const prev = parseLifeSeconds(
    previousLife !== undefined && previousLife !== null
      ? previousLife
      : obj.userData?.life
  );

  // No meaningful change (treat null/undefined as equal).
  if (next === prev) {
    // Property edits must never clear an already-finished lifecycle.
    if (obj.userData?.lifeExpired === true && next != null) {
      return false;
    }
    return false;
  }
  // Both missing.
  if (next == null && prev == null) return false;

  // Expired + same effective duration after coerce.
  if (
    obj.userData?.lifeExpired === true &&
    next != null &&
    next === parseLifeSeconds(obj.userData?.life)
  ) {
    return false;
  }

  clearLifeTimeout(obj);

  if (obj.userData) {
    obj.userData.life = next;
  }

  if (next != null && next > 0) {
    setLifeTimeout(obj, next);
    applyParticleRuntimeHidden(obj, false);
    obj.visible = true;
    if (obj.userData) {
      obj.userData.lifeExpired = false;
      obj.userData.isExpired = false;
      obj.userData.runtimeHidden = false;
      obj.userData.isStopped = false;
      obj.userData.isPlaying = true;
      obj.userData.isPaused = false;
      // Stars / animate-loop based remaining life
      if (options.resetRemainingLife !== false) {
        obj.userData.remainingLife = next;
      }
    }

    if (options.restartAnimation !== false) {
      const animateFunction = obj.userData?.animateFunction;
      if (typeof animateFunction === 'function') {
        try {
          startAnimation(obj, animateFunction, obj.name || obj.uuid);
        } catch (_) {
          /* ignore */
        }
      }
    }
    return true;
  }

  // Life cleared or set to 0 — stop and hide (preview only effectively).
  try {
    stopAnimation(obj, obj.name);
  } catch (_) {
    /* ignore */
  }
  applyParticleRuntimeHidden(obj, true);
  if (obj.userData) {
    obj.userData.lifeExpired = true;
    obj.userData.isStopped = true;
    obj.userData.isPlaying = false;
    obj.userData.remainingLife = 0;
  }
  return true;
}

/** Clear all active life timers on scene particles (e.g. leaving Preview). */
function clearSceneParticleLifeTimeouts(scene) {
  if (!scene?.traverse) return;
  scene.traverse((obj) => {
    if (obj?.deleteType === 'particle' || obj?.userData?._lifeTimeoutId) {
      clearLifeTimeout(obj);
      if (obj.userData) {
        const life = parseLifeSeconds(obj.userData.life);
        if (life != null && life > 0) {
          obj.userData.remainingLife = life;
        }
        obj.userData.lifeExpired = false;
        obj.userData.isExpired = false;
        obj.userData.runtimeHidden = false;
      }
      applyParticleRuntimeHidden(obj, false);
    }
  });
}

export {
  setLifeTimeout,
  clearLifeTimeout,
  parseLifeSeconds,
  applyLifeTimeoutIfChanged,
  isParticleLifecycleEnabled,
  isParticleLifecycleSuspendedInDesign,
  suspendParticleLifecycleForSelection,
  resumeParticleLifecycleAfterSelection,
  expireParticleLifecycle,
  clearSceneParticleLifeTimeouts,
};
